import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'
import { Trash2, Plus } from 'lucide-react'
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'

const money = (v: number | null | undefined) => (v == null ? '—' : Math.round(v).toLocaleString())
const mult = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(2)}x`)
const pct = (v: number | null | undefined) => (v == null ? '—' : `${(v * 100).toFixed(1)}%`)
const inp = 'rounded-md border border-gray-300 px-2 py-1 text-sm'
const btn = 'flex items-center gap-1 rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50'
const th = 'px-3 py-2 text-left text-xs font-medium text-gray-500 whitespace-nowrap'
const td = 'px-3 py-2 text-sm text-gray-800 whitespace-nowrap'
const CCYS = ['USD', 'EUR', 'GBP', 'CHF']

// ============================================================ Commitments & Calls
export function CommitmentsTab({ portfolioId, ccy }: { portfolioId: string; ccy: string }) {
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['pa-capital', portfolioId, ccy],
    queryFn: () => api.get(`/private/portfolios/${portfolioId}/capital-summary`, { params: { report_ccy: ccy } }).then(r => r.data),
    enabled: !!portfolioId,
  })
  const refresh = () => qc.invalidateQueries({ queryKey: ['pa-capital'] })
  const [cm, setCm] = useState<any>({ vehicle: '', committed_amount: '', currency: 'USD', start_date: '' })
  const [cl, setCl] = useState<any>({ call_date: '', amount: '', currency: 'USD' })
  const s = q.data?.summary
  const addCommitment = async () => { await api.post(`/private/portfolios/${portfolioId}/commitments`, { ...cm, committed_amount: Number(cm.committed_amount || 0) }); setCm({ vehicle: '', committed_amount: '', currency: 'USD', start_date: '' }); refresh() }
  const addCall = async () => { await api.post(`/private/portfolios/${portfolioId}/capital-calls`, { ...cl, amount: Number(cl.amount || 0) }); setCl({ call_date: '', amount: '', currency: 'USD' }); refresh() }
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[['Committed', s?.committed], ['Called', s?.called], ['Uncalled', s?.uncalled]].map(([l, v]: any) => (
          <div key={l} className="rounded-lg border border-gray-200 p-3"><div className="text-xs text-gray-400">{l} · {ccy}</div><div className="text-xl font-semibold">{money(v)}</div></div>
        ))}
        <div className="rounded-lg border border-gray-200 p-3"><div className="text-xs text-gray-400">Called %</div><div className="text-xl font-semibold">{s?.called_pct == null ? '—' : `${s.called_pct.toFixed(0)}%`}</div></div>
      </div>

      <div>
        <div className="mb-1 text-sm font-medium">Commitments</div>
        <div className="mb-2 flex flex-wrap items-end gap-2">
          <input className={inp} placeholder="Vehicle / fund" value={cm.vehicle} onChange={e => setCm({ ...cm, vehicle: e.target.value })} />
          <input className={inp} type="number" placeholder="Committed" value={cm.committed_amount} onChange={e => setCm({ ...cm, committed_amount: e.target.value })} />
          <select className={inp} value={cm.currency} onChange={e => setCm({ ...cm, currency: e.target.value })}>{CCYS.map(c => <option key={c}>{c}</option>)}</select>
          <input className={inp} type="date" value={cm.start_date} onChange={e => setCm({ ...cm, start_date: e.target.value })} />
          <button className={btn} onClick={addCommitment}><Plus size={14} />Add</button>
        </div>
        <table className="min-w-full divide-y divide-gray-200"><thead><tr>{['Vehicle', 'Committed', 'Ccy', 'Start', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-100">{(q.data?.commitments || []).map((c: any) => (
            <tr key={c.id}><td className={td}>{c.vehicle || '—'}</td><td className={td}>{money(c.committed_amount)}</td><td className={td}>{c.currency}</td><td className={td}>{c.start_date || '—'}</td>
              <td className={td}><button className="text-gray-400 hover:text-red-600" onClick={async () => { await api.delete(`/private/commitments/${c.id}`); refresh() }}><Trash2 size={14} /></button></td></tr>
          ))}</tbody></table>
      </div>

      <div>
        <div className="mb-1 text-sm font-medium">Capital calls (drawdowns)</div>
        <div className="mb-2 flex flex-wrap items-end gap-2">
          <input className={inp} type="date" value={cl.call_date} onChange={e => setCl({ ...cl, call_date: e.target.value })} />
          <input className={inp} type="number" placeholder="Amount" value={cl.amount} onChange={e => setCl({ ...cl, amount: e.target.value })} />
          <select className={inp} value={cl.currency} onChange={e => setCl({ ...cl, currency: e.target.value })}>{CCYS.map(c => <option key={c}>{c}</option>)}</select>
          <button className={btn} disabled={!cl.call_date} onClick={addCall}><Plus size={14} />Add</button>
        </div>
        <table className="min-w-full divide-y divide-gray-200"><thead><tr>{['Date', 'Amount', 'Ccy', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-100">{(q.data?.calls || []).map((c: any) => (
            <tr key={c.id}><td className={td}>{c.call_date}</td><td className={td}>{money(c.amount)}</td><td className={td}>{c.currency}</td>
              <td className={td}><button className="text-gray-400 hover:text-red-600" onClick={async () => { await api.delete(`/private/capital-calls/${c.id}`); refresh() }}><Trash2 size={14} /></button></td></tr>
          ))}</tbody></table>
      </div>
    </div>
  )
}

// ============================================================ Company KPIs
export function KpisTab({ positions }: { positions: any[] }) {
  const [invId, setInvId] = useState<string>(positions[0]?.id || '')
  const qc = useQueryClient()
  const q = useQuery({
    queryKey: ['pa-kpis', invId],
    queryFn: () => api.get(`/private/investments/${invId}/kpis`).then(r => r.data.kpis),
    enabled: !!invId,
  })
  const [f, setF] = useState<any>({ period: '', revenue: '', burn: '', runway_months: '', headcount: '' })
  const add = async () => {
    await api.post(`/private/investments/${invId}/kpis`, {
      period: f.period, revenue: num(f.revenue), burn: num(f.burn),
      runway_months: num(f.runway_months), headcount: f.headcount === '' ? null : parseInt(f.headcount),
    })
    setF({ period: '', revenue: '', burn: '', runway_months: '', headcount: '' })
    qc.invalidateQueries({ queryKey: ['pa-kpis', invId] })
  }
  const num = (v: any) => v === '' ? null : Number(v)
  const rows = q.data || []
  return (
    <div className="space-y-4">
      <select className={inp} value={invId} onChange={e => setInvId(e.target.value)}>
        {positions.map(p => <option key={p.id} value={p.id}>{p.startup_name}</option>)}
      </select>
      <div className="flex flex-wrap items-end gap-2">
        <input className={inp} type="date" value={f.period} onChange={e => setF({ ...f, period: e.target.value })} />
        <input className={inp} type="number" placeholder="Revenue" value={f.revenue} onChange={e => setF({ ...f, revenue: e.target.value })} />
        <input className={inp} type="number" placeholder="Burn" value={f.burn} onChange={e => setF({ ...f, burn: e.target.value })} />
        <input className={inp} type="number" placeholder="Runway (mo)" value={f.runway_months} onChange={e => setF({ ...f, runway_months: e.target.value })} />
        <input className={inp} type="number" placeholder="Headcount" value={f.headcount} onChange={e => setF({ ...f, headcount: e.target.value })} />
        <button className={btn} disabled={!f.period} onClick={add}><Plus size={14} />Add KPI</button>
      </div>
      {rows.length > 1 && (
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={rows} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" /><XAxis dataKey="period" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} /><Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="revenue" stroke="#2563eb" name="Revenue" /><Line type="monotone" dataKey="burn" stroke="#ef4444" name="Burn" />
          </LineChart>
        </ResponsiveContainer>
      )}
      <table className="min-w-full divide-y divide-gray-200"><thead><tr>{['Period', 'Revenue', 'Burn', 'Runway (mo)', 'Headcount', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">{rows.map((k: any) => (
          <tr key={k.id}><td className={td}>{k.period}</td><td className={td}>{money(k.revenue)}</td><td className={td}>{money(k.burn)}</td><td className={td}>{k.runway_months ?? '—'}</td><td className={td}>{k.headcount ?? '—'}</td>
            <td className={td}><button className="text-gray-400 hover:text-red-600" onClick={async () => { await api.delete(`/private/kpis/${k.id}`); qc.invalidateQueries({ queryKey: ['pa-kpis', invId] }) }}><Trash2 size={14} /></button></td></tr>
        ))}{!rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No KPIs yet.</td></tr>}</tbody></table>
    </div>
  )
}

// ============================================================ Deal Flow (CRM)
const PIPELINE = ['sourced', 'screening', 'due_diligence', 'term_sheet', 'invested', 'passed']
export function DealsTab({ portfolioId }: { portfolioId: string }) {
  const qc = useQueryClient()
  const q = useQuery({ queryKey: ['pa-deals', portfolioId], queryFn: () => api.get(`/private/portfolios/${portfolioId}/deals`).then(r => r.data.deals), enabled: !!portfolioId })
  const [f, setF] = useState<any>({ name: '', sector: '', stage: '', pipeline_status: 'sourced', ask_amount: '', lead: '' })
  const refresh = () => qc.invalidateQueries({ queryKey: ['pa-deals', portfolioId] })
  const add = async () => { await api.post(`/private/portfolios/${portfolioId}/deals`, { ...f, ask_amount: f.ask_amount === '' ? null : Number(f.ask_amount) }); setF({ name: '', sector: '', stage: '', pipeline_status: 'sourced', ask_amount: '', lead: '' }); refresh() }
  const setStatus = async (d: any, status: string) => { await api.put(`/private/deals/${d.id}`, { ...d, pipeline_status: status }); refresh() }
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <input className={inp} placeholder="Company" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} />
        <input className={inp} placeholder="Sector" value={f.sector} onChange={e => setF({ ...f, sector: e.target.value })} />
        <input className={inp} type="number" placeholder="Ask" value={f.ask_amount} onChange={e => setF({ ...f, ask_amount: e.target.value })} />
        <input className={inp} placeholder="Lead" value={f.lead} onChange={e => setF({ ...f, lead: e.target.value })} />
        <select className={inp} value={f.pipeline_status} onChange={e => setF({ ...f, pipeline_status: e.target.value })}>{PIPELINE.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select>
        <button className={btn} disabled={!f.name} onClick={add}><Plus size={14} />Add deal</button>
      </div>
      <table className="min-w-full divide-y divide-gray-200"><thead><tr>{['Company', 'Sector', 'Ask', 'Lead', 'Status', ''].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody className="divide-y divide-gray-100">{(q.data || []).map((d: any) => (
          <tr key={d.id} className="hover:bg-gray-50"><td className={`${td} font-medium`}>{d.name}</td><td className={td}>{d.sector || '—'}</td><td className={td}>{money(d.ask_amount)}</td><td className={td}>{d.lead || '—'}</td>
            <td className={td}><select className="rounded border border-gray-200 px-1 py-0.5 text-xs" value={d.pipeline_status} onChange={e => setStatus(d, e.target.value)}>{PIPELINE.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}</select></td>
            <td className={td}><button className="text-gray-400 hover:text-red-600" onClick={async () => { await api.delete(`/private/deals/${d.id}`); refresh() }}><Trash2 size={14} /></button></td></tr>
        ))}{!(q.data || []).length && <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No deals in the pipeline.</td></tr>}</tbody></table>
    </div>
  )
}

// ============================================================ Fund Model
export function FundModelTab() {
  const [f, setF] = useState<any>({ fund_size: 100000000, avg_check: 1000000, mgmt_fee_pct: 0.02, fund_life_years: 10, reserve_ratio: 0.5, avg_graduation: 0.5, target_gross_multiple: 3, target_ownership_pct: 8 })
  const [res, setRes] = useState<any>(null)
  const compute = async () => setRes((await api.post('/private/fund-model', f)).data)
  const set = (k: string) => (e: any) => setF({ ...f, [k]: Number(e.target.value) })
  const rows: [string, string][] = [['fund_size', 'Fund size'], ['avg_check', 'Avg check'], ['mgmt_fee_pct', 'Mgmt fee (0-1)'], ['fund_life_years', 'Fund life (yrs)'], ['reserve_ratio', 'Reserve ratio (0-1)'], ['avg_graduation', 'Avg graduation (0-1)'], ['target_gross_multiple', 'Target gross MOIC'], ['target_ownership_pct', 'Target ownership %']]
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        {rows.map(([k, l]) => (
          <div key={k} className="flex items-center justify-between gap-3"><label className="text-sm text-gray-600">{l}</label><input className={`${inp} w-40`} type="number" step="any" value={f[k]} onChange={set(k)} /></div>
        ))}
        <button className={btn} onClick={compute}><Plus size={14} />Compute</button>
      </div>
      {res && (
        <div className="space-y-1 rounded-lg border border-gray-200 p-4 text-sm">
          {[['Management fees (total)', money(res.mgmt_fees_total)], ['Investable capital', money(res.investable_capital)], ['Initial allocation', money(res.initial_allocation)], ['Reserve allocation', money(res.reserve_allocation)], ['# initial deals', res.n_initial_deals], ['Graduation-weighted follow-on need', money(res.graduation_weighted_followon_need)], ['Reserve surplus / deficit', money(res.reserve_surplus_deficit)], ['Projected gross value', money(res.projected_gross_value)], ['Projected gross TVPI', mult(res.projected_gross_tvpi)], ['Implied entry valuation', money(res.implied_entry_valuation)]].map(([l, v]: any) => (
            <div key={l} className="flex justify-between border-b border-gray-100 py-1"><span className="text-gray-500">{l}</span><span className="font-medium">{v}</span></div>
          ))}
        </div>
      )}
    </div>
  )
}

// ============================================================ Waterfall
export function WaterfallTab({ portfolioId, ccy }: { portfolioId: string; ccy: string }) {
  const [hurdle, setHurdle] = useState(0.08)
  const [carry, setCarry] = useState(0.20)
  const [catchup, setCatchup] = useState(true)
  const q = useQuery({
    queryKey: ['pa-waterfall', portfolioId, ccy, hurdle, carry, catchup],
    queryFn: () => api.get(`/private/portfolios/${portfolioId}/waterfall`, { params: { hurdle, carry, catchup, report_ccy: ccy } }).then(r => r.data),
    enabled: !!portfolioId,
  })
  const w = q.data
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm">Hurdle <input className={`${inp} w-24`} type="number" step="0.01" value={hurdle} onChange={e => setHurdle(Number(e.target.value))} /></label>
        <label className="text-sm">Carry <input className={`${inp} w-24`} type="number" step="0.01" value={carry} onChange={e => setCarry(Number(e.target.value))} /></label>
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={catchup} onChange={e => setCatchup(e.target.checked)} />GP catch-up</label>
      </div>
      {w && (
        <>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="rounded-md border border-gray-200 px-4 py-2">Paid-in <b>{money(w.paid_in)}</b></div>
            <div className="rounded-md border border-gray-200 px-4 py-2">Distributed <b>{money(w.distributed)}</b></div>
            <div className="rounded-md border border-gray-200 px-4 py-2">LP total <b className="text-emerald-600">{money(w.lp_total)}</b></div>
            <div className="rounded-md border border-gray-200 px-4 py-2">GP total <b className="text-blue-600">{money(w.gp_total)}</b></div>
            <div className="rounded-md border border-gray-200 px-4 py-2">Effective carry <b>{pct(w.gp_carry_effective)}</b></div>
          </div>
          <table className="min-w-full divide-y divide-gray-200"><thead><tr>{['Tier', 'LP', 'GP'].map(h => <th key={h} className={th}>{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-gray-100">{w.tiers.map((t: any, i: number) => (
              <tr key={i}><td className={`${td} font-medium`}>{t.tier}</td><td className={td}>{money(t.lp)}</td><td className={td}>{money(t.gp)}</td></tr>
            ))}</tbody></table>
        </>
      )}
    </div>
  )
}

// ============================================================ Benchmark (PME)
export function PmeTab({ portfolioId, ccy }: { portfolioId: string; ccy: string }) {
  const [index, setIndex] = useState('SPY')
  const [applied, setApplied] = useState('SPY')
  const q = useQuery({
    queryKey: ['pa-pme', portfolioId, ccy, applied],
    queryFn: () => api.get(`/private/portfolios/${portfolioId}/pme`, { params: { index: applied, report_ccy: ccy } }).then(r => r.data),
    enabled: !!portfolioId,
  })
  const d = q.data
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2">
        <label className="text-sm">Benchmark index <input className={`${inp} w-32`} value={index} onChange={e => setIndex(e.target.value)} /></label>
        <button className={btn} onClick={() => setApplied(index)}>Run PME</button>
      </div>
      {q.isFetching && <div className="text-xs text-gray-400">Computing…</div>}
      {d && d.available === false && <div className="rounded bg-amber-50 px-3 py-2 text-sm text-amber-700">{d.reason}</div>}
      {d && d.available !== false && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-4">
            <div className="rounded-lg border border-gray-200 p-4"><div className="text-xs text-gray-400">KS-PME</div><div className={`text-2xl font-bold ${(d.ks_pme ?? 0) >= 1 ? 'text-emerald-600' : 'text-red-500'}`}>{d.ks_pme ?? '—'}</div><div className="text-xs text-gray-400">{(d.ks_pme ?? 0) >= 1 ? 'outperformed' : 'underperformed'} index</div></div>
            <div className="rounded-lg border border-gray-200 p-4"><div className="text-xs text-gray-400">Direct Alpha (annualized)</div><div className="text-2xl font-bold">{d.direct_alpha == null ? '—' : `${(d.direct_alpha * 100).toFixed(1)}%`}</div></div>
          </div>
          <p className="text-xs text-gray-500">Index: {d.index_source} · window {d.start} → {d.end}. KS-PME &gt; 1.0 means the portfolio beat the public index on a capital-timing-adjusted basis.</p>
        </div>
      )}
    </div>
  )
}
